<template>
  <aside class="fg-summary">
    <div v-if="!outwardAv" class="fg-summary__empty">
      <Icon name="info" :size="20" />
      <p>{{ t('summary.empty') }}</p>
    </div>

    <template v-else>
      <header class="fg-summary__title">{{ t('summary.title') }}</header>

      <!-- TRAJET ALLER -->
      <section class="fg-summary__section">
        <header class="fg-summary__head">
          <Icon name="arrow-right" :size="13" />
          <span>{{ t('summary.outward') }}</span>
        </header>
        <p class="fg-summary__route">
          {{ formatPort(outwardAv.depart_name) }}
          <Icon name="arrow-right" :size="13" class="fg-summary__route-arrow" />
          {{ formatPort(outwardAv.arrive_name) }}
        </p>

        <!-- Horaire départ + arrivée + durée -->
        <div class="fg-summary__schedule">
          <div class="fg-summary__schedule-row">
            <span class="fg-summary__schedule-label">{{ t('summary.outward') }}</span>
            <span class="fg-summary__schedule-value">
              {{ formatDateHuman(outwardAv.depart_at, locale) }}
              · {{ formatTime(outwardAv.depart_at, locale) }}
            </span>
          </div>
          <div v-if="outwardDuration" class="fg-summary__schedule-row">
            <span class="fg-summary__schedule-label">{{ t('summary.duration') }}</span>
            <span class="fg-summary__schedule-value">{{ outwardDuration }}</span>
          </div>
        </div>

        <p class="fg-summary__meta">
          <img v-if="outwardAv.company?.logo" :src="outwardAv.company.logo" :alt="outwardAv.company.name || ''" class="fg-summary__logo" />
          <span>{{ humanizeLabel(outwardAv.company?.name) }}</span>
          <span v-if="outwardAv.ship_name" class="fg-summary__dot">·</span>
          <span v-if="outwardAv.ship_name">{{ humanizeLabel(outwardAv.ship_name) }}</span>
        </p>

        <!-- Prix de l'ALLER dans le bloc Aller -->
        <div v-if="outwardTariff" class="fg-summary__leg-price">
          <span class="fg-summary__leg-price-label">{{ tariffLabel(outwardTariff.type) }}</span>
          <span class="fg-summary__price-amount">{{ formatPrice(outwardAmount, currencyOf(outwardTariff), locale) }}</span>
        </div>
        <!-- Sous-prix par groupe (dossier > 4 pax) : détail group 1 / group 2. -->
        <div v-if="outwardGroupPrices.length > 1" class="fg-summary__subprices">
          <div v-for="gp in outwardGroupPrices" :key="`out-g${gp.group}`" class="fg-summary__subprice">
            <span class="fg-summary__subprice-label">{{ t('summary.group', { group: gp.group }) }}</span>
            <span class="fg-summary__subprice-amount">{{ formatPrice(amountOf(gp.tariff), currencyOf(gp.tariff), locale) }}</span>
          </div>
        </div>

        <!-- Repas choisis pour l'ALLER, dans le bloc Aller. -->
        <div
          v-for="m in outwardMeals"
          :key="m.key"
          class="fg-summary__leg-price fg-summary__leg-price--meal"
        >
          <span class="fg-summary__leg-price-label">{{ m.label }} · ×{{ m.quantity }}</span>
          <span class="fg-summary__price-amount">{{ formatPrice(m.amount, m.currency, locale) }}</span>
        </div>
      </section>

      <!-- PASSAGERS -->
      <section v-if="passengerSummary" class="fg-summary__section">
        <header class="fg-summary__head">
          <Icon name="users" :size="13" />
          <span>{{ t('summary.passengers') }}</span>
        </header>
        <p class="fg-summary__text">{{ passengerSummary }}</p>
      </section>

      <!-- VÉHICULE -->
      <section v-if="store.search.vehicle?.type" class="fg-summary__section">
        <header class="fg-summary__head">
          <Icon name="car" :size="13" />
          <span>{{ t('summary.vehicle') }}</span>
        </header>
        <p class="fg-summary__text fg-summary__veh">
          <img v-if="vehicleLogo" :src="vehicleLogo" :alt="vehicleLabel" class="fg-summary__veh-logo" />
          <strong>{{ vehicleLabel }}</strong>
          <span v-if="store.search.vehicle?.priority"> · {{ t('vehicle.priority.priority') }}</span>
        </p>
      </section>

      <!-- PLACE ALLER (group 1 + group 2 si > 4 pax) -->
      <section v-if="outwardPlacesLabel" class="fg-summary__section">
        <header class="fg-summary__head">
          <Icon name="seat" :size="13" />
          <span>{{ t('summary.placeOutward') }}</span>
        </header>
        <p class="fg-summary__text">{{ outwardPlacesLabel }}</p>
      </section>

      <!-- TRAJET RETOUR (si A/R) -->
      <template v-if="returnAv">
        <section class="fg-summary__section">
          <header class="fg-summary__head">
            <Icon name="arrow-left" :size="13" />
            <span>{{ t('summary.return') }}</span>
          </header>
          <p class="fg-summary__route">
            {{ formatPort(returnAv.depart_name) }}
            <Icon name="arrow-right" :size="13" class="fg-summary__route-arrow" />
            {{ formatPort(returnAv.arrive_name) }}
          </p>
          <div class="fg-summary__schedule">
            <div class="fg-summary__schedule-row">
              <span class="fg-summary__schedule-label">{{ t('summary.return') }}</span>
              <span class="fg-summary__schedule-value">
                {{ formatDateHuman(returnAv.depart_at, locale) }}
                · {{ formatTime(returnAv.depart_at, locale) }}
              </span>
            </div>
            <div v-if="returnDuration" class="fg-summary__schedule-row">
            <span class="fg-summary__schedule-label">{{ t('summary.duration') }}</span>
            <span class="fg-summary__schedule-value">{{ returnDuration }}</span>
          </div>
          </div>
          <p class="fg-summary__meta">
            <img v-if="returnAv.company?.logo" :src="returnAv.company.logo" :alt="returnAv.company.name || ''" class="fg-summary__logo" />
            <span>{{ humanizeLabel(returnAv.company?.name) }}</span>
            <span v-if="returnAv.ship_name" class="fg-summary__dot">·</span>
            <span v-if="returnAv.ship_name">{{ humanizeLabel(returnAv.ship_name) }}</span>
          </p>

          <!-- Prix du RETOUR dans le bloc Retour -->
          <div v-if="returnTariff" class="fg-summary__leg-price">
            <span class="fg-summary__leg-price-label">{{ tariffLabel(returnTariff.type) }}</span>
            <span class="fg-summary__price-amount">{{ formatPrice(returnAmount, currencyOf(returnTariff), locale) }}</span>
          </div>
          <!-- Sous-prix par groupe (dossier > 4 pax). -->
          <div v-if="returnGroupPrices.length > 1" class="fg-summary__subprices">
            <div v-for="gp in returnGroupPrices" :key="`ret-g${gp.group}`" class="fg-summary__subprice">
              <span class="fg-summary__subprice-label">{{ t('summary.group', { group: gp.group }) }}</span>
              <span class="fg-summary__subprice-amount">{{ formatPrice(amountOf(gp.tariff), currencyOf(gp.tariff), locale) }}</span>
            </div>
          </div>

          <!-- Repas choisis pour le RETOUR, dans le bloc Retour. -->
          <div
            v-for="m in returnMeals"
            :key="m.key"
            class="fg-summary__leg-price fg-summary__leg-price--meal"
          >
            <span class="fg-summary__leg-price-label">{{ m.label }} · ×{{ m.quantity }}</span>
            <span class="fg-summary__price-amount">{{ formatPrice(m.amount, m.currency, locale) }}</span>
          </div>
        </section>

        <!-- VÉHICULE (porté A/R : rappelé aussi côté retour) -->
        <section v-if="store.search.vehicle?.type" class="fg-summary__section">
          <header class="fg-summary__head">
            <Icon name="car" :size="13" />
            <span>{{ t('summary.vehicle') }}</span>
          </header>
          <p class="fg-summary__text fg-summary__veh">
            <img v-if="vehicleLogo" :src="vehicleLogo" :alt="vehicleLabel" class="fg-summary__veh-logo" />
            <strong>{{ vehicleLabel }}</strong>
            <span v-if="store.search.vehicle?.priority"> · {{ t('vehicle.priority.priority') }}</span>
          </p>
        </section>

        <section v-if="returnPlacesLabel" class="fg-summary__section">
          <header class="fg-summary__head">
            <Icon name="seat" :size="13" />
            <span>{{ t('summary.placeReturn') }}</span>
          </header>
          <p class="fg-summary__text">{{ returnPlacesLabel }}</p>
        </section>
      </template>

      <!-- TOTAL — mis en avant (prix par sens affichés dans chaque bloc trajet) -->
      <section class="fg-summary__total">
        <div class="fg-summary__total-head">
          <span class="fg-summary__total-label">{{ t('summary.total') }}</span>
          <span class="fg-summary__total-value">{{ totalFormatted }}</span>
        </div>
        <span class="fg-summary__total-note">{{ t('summary.taxesIncluded') }}</span>
      </section>

      <!-- Rappel CTA (optionnel, plus petit) — total intégré au bouton. -->
      <div v-if="showContinue" class="fg-summary__cta-wrap">
        <button
          type="button"
          class="fg-btn fg-btn--primary fg-btn--block fg-summary__cta"
          :disabled="continueDisabled"
          @click="emit('continue')"
        >{{ t('results.continue') }} · {{ totalFormatted }}</button>
      </div>
    </template>
  </aside>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import { useStore } from '../../store'
import { useI18n } from '../../i18n'
import { computeBookingTotal, computeCrossingDuration } from '../../../../shared/rules'
import { PassengerType } from '../../../../shared/types/enums'
import type { Availability } from '../../../../shared/types'
import { formatDuration, formatPrice, formatDate, formatTime, formatPortUpper as formatPort, humanizeLabel, normalizeBrandLogo } from '../../utils/format'
import { useSelection } from '../../composables/useSelection'
import Icon from '../Icon.vue'

// CTA « Continuer » optionnel en bas du résumé (rappel du bouton principal).
defineProps<{ showContinue?: boolean; continueDisabled?: boolean }>()
const emit = defineEmits<{ continue: [] }>()

const store = useStore()
const { t, locale } = useI18n()

// Sélection courante (av représentative + tarifs + places, groupes 1 & 2) —
// composable PARTAGÉ avec Confirmation (selectedAv / placesLabel / tariffLabel…).
const {
  outwardAv, returnAv,
  outwardTariff, returnTariff, allTariffs, outwardAmount, returnAmount,
  outwardGroupPrices, returnGroupPrices,
  outwardPlacesLabel, returnPlacesLabel,
  outwardMeals, returnMeals, mealsAmount,
  tariffLabel, amountOf, currencyOf,
} = useSelection()

// Logo de marque : priorité au vehicle enrichi par le backend dans l'availability
// sélectionnée (toujours présent), fallback sur le catalogue brand (drawer).
// normalizeBrandLogo neutralise la transfo Cloudinary `g_face` qui casse l'image.
const vehicleLogo = computed<string>(() =>
  normalizeBrandLogo(outwardAv.value?.vehicle?.logo || store.search.vehicle?.logo),
)

const passengerSummary = computed(() => {
  const counts = { adults: 0, children: 0, babies: 0 }
  for (const p of store.search.passengers) {
    if (p.type === PassengerType.Adult) counts.adults++
    else if (p.type === PassengerType.Child) counts.children++
    else if (p.type === PassengerType.Baby) counts.babies++
  }
  const parts: string[] = []
  if (counts.adults > 0) parts.push(`${counts.adults} ${t(counts.adults === 1 ? 'summary.adult' : 'summary.adults')}`)
  if (counts.children > 0) parts.push(`${counts.children} ${t(counts.children === 1 ? 'summary.child' : 'summary.children')}`)
  if (counts.babies > 0) parts.push(`${counts.babies} ${t(counts.babies === 1 ? 'summary.baby' : 'summary.babies')}`)
  return parts.join(', ')
})

const vehicleLabel = computed(() => {
  const v = store.search.vehicle
  if (!v?.type) return ''
  // /!\ Vehicle.brand contient l'UUID (legacy alias de brand_id) ; le nom
  // humain de la marque est dans `brand_name`. Bug typique : afficher
  // "401ae831-9e98-... T-Roc" au lieu de "Renault Clio".
  const bits = [v.brand_name, v.model].filter(Boolean)
  return bits.length > 0 ? bits.join(' ') : v.type
})

// Total = TOUS les groupes (1 + 2) + repas choisis. Sommer le seul groupe 1 ou
// omettre les repas sous-facturerait le résumé.
const totalFormatted = computed(() => {
  const total = computeBookingTotal({ tariffs: allTariffs.value })
  return formatPrice(total.amount + mealsAmount.value, total.currency, locale.value)
})

// Durée : champ provider en PRIORITÉ (timezones « menteuses » dans les ISO →
// un diff Date.parse afficherait 44 h là où l'étape Traversée affiche 45 h).
const outwardDuration = computed<string>(() => durationFor(outwardAv.value))
const returnDuration = computed<string>(() => durationFor(returnAv.value))

function durationFor(av: Availability | null): string {
  if (!av) return ''
  return formatDuration(av.duration) || computeDuration(av.depart_at, av.arrive_at)
}

// Date format humain : "Mer. 29 juil." plutôt que "29/07/2026" pur numérique.
function formatDateHuman(raw: string | null | undefined, lang: string): string {
  if (!raw) return ''
  try {
    const d = new Date(raw)
    if (!Number.isFinite(d.getTime())) return formatDate(raw, lang)
    return d.toLocaleDateString(lang, { weekday: 'short', day: 'numeric', month: 'short' })
  } catch {
    return formatDate(raw, lang)
  }
}

// Calcul durée traversée à partir de depart_at + arrive_at.
// Source primaire : différence horaire brute. Si negative ou aberrante,
// on retourne '' (pas d'affichage trompeur).
function computeDuration(depart: string | null | undefined, arrive: string | null | undefined): string {
  const hours = computeCrossingDuration(depart, arrive)
  return hours === null ? '' : formatDuration(Math.round(hours * 60))
}
</script>
